import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Backdrop
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import ApiService from '../../services/api.service';
import internalServiceService from '../../services/internal-service.service';
import WorkOrderCustomerForm from '../../components/work-orders/WorkOrderCustomerForm';
import {
  createCustomer, toSelectableService, validateCustomer, buildAddCustomerPayload,
  uploadCustomerDocuments, syncCustomerEmployee, customerHasIdentifier
} from '../../components/work-orders/workOrderCustomer';

/**
 * "Add customer" tab on the work order detail page: the Create Work Order customer form for one new
 * applicant. On submit it adds the customer and all their services in one call (tasks + totals are
 * handled server-side), then uploads their documents to the work order and runs Thaklis employee
 * tracking — the same steps work order create runs, without creating a new work order.
 */
const AddWorkOrderCustomerPanel = ({
  workOrder, currentUser, documentTypes = [], showThaklisServices = false, existingCustomerNames = [],
  onAdded, onCancel
}) => {
  const [customer, setCustomer] = useState(createCustomer);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [error, setError] = useState(null);
  const [confirmNoIdOpen, setConfirmNoIdOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState('');

  const workOrderId = workOrder?.id;
  const customerType = workOrder?.customer_type || workOrder?.customerType || 'organization';
  const organizationId = workOrder?.organization_id || workOrder?.organizationId || null;
  const mappedOrganizationId = workOrder?.mapped_organization_id || workOrder?.mappedOrganizationId || null;

  // Employee tracking: Thaklis features on, a mapped Thaklis org, and not a B2B provider's
  // typing-center customer (same rule as work order create, where the org's type is unknown here).
  const employeeTrackingActive = currentUser?.thaklisEnabled !== false
    && !!mappedOrganizationId
    && (customerType !== 'organization' || currentUser?.organizationType !== 'B2B');

  // Services priced for this work order's client (walk-in → uniform services only).
  useEffect(() => {
    let active = true;
    setLoadingServices(true);
    internalServiceService.getServicesForLineItems(customerType === 'organization' ? organizationId : null)
      .then((list) => { if (active) setServices((list || []).map(toSelectableService)); })
      .catch(() => { if (active) setServices([]); })
      .finally(() => { if (active) setLoadingServices(false); });
    return () => { active = false; };
  }, [customerType, organizationId]);

  const handleSubmit = () => {
    setError(null);
    const { error: invalid } = validateCustomer(customer);
    if (invalid) { setError(invalid); return; }
    if (customer.documents.some(d => d.extracting)) {
      setError('Please wait — documents are still being read.');
      return;
    }
    const name = customer.name.trim().toLowerCase();
    if (existingCustomerNames.some(n => (n || '').trim().toLowerCase() === name)) {
      setError(`"${customer.name.trim()}" is already a customer on this work order — add services to them instead.`);
      return;
    }
    if (employeeTrackingActive && !customerHasIdentifier(customer)) {
      setConfirmNoIdOpen(true);
      return;
    }
    proceed();
  };

  const proceed = async () => {
    setConfirmNoIdOpen(false);
    setSubmitting(true);
    setError(null);
    try {
      // 1. Customer + services (one transaction — all or nothing).
      setStage('Adding customer and services…');
      await ApiService.addWorkOrderCustomer(workOrderId, buildAddCustomerPayload(customer));

      // 2. Documents to the work order record, tagged to the customer.
      let failedDocIds = new Set();
      if (customer.documents.length > 0) {
        setStage('Uploading documents…');
        failedDocIds = await uploadCustomerDocuments(workOrderId, customer);
      }

      // 3. Thaklis employee tracking (best-effort — never undoes the added customer).
      let syncFailed = false;
      if (employeeTrackingActive) {
        setStage('Tracking employee documents…');
        try {
          await syncCustomerEmployee({
            customer,
            customerType,
            mappedOrganizationId,
            internalOrganizationId: customerType === 'organization' ? organizationId : null,
            failedDocIds,
            documentTypes
          });
        } catch (err) {
          console.error('Employee/document sync failed:', err);
          syncFailed = true;
        }
      }

      const warnings = [];
      if (failedDocIds.size > 0) {
        warnings.push(`${failedDocIds.size} document(s) failed to upload — re-upload them from the customer's documents`);
      }
      if (syncFailed) warnings.push('the employee could not be tracked in Thaklis');
      onAdded && onAdded({ name: customer.name.trim(), warning: warnings.length ? warnings.join('; ') : null });
    } catch (err) {
      console.error('Add customer failed:', err);
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to add the customer');
    } finally {
      setSubmitting(false);
      setStage('');
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loadingServices ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : (
        <WorkOrderCustomerForm
          customer={customer}
          onChange={setCustomer}
          availableServices={services}
          documentTypes={documentTypes}
          showThaklisServices={showThaklisServices}
          employeeTrackingActive={employeeTrackingActive}
          onError={setError}
        />
      )}

      <Paper variant="outlined" sx={{ mt: 3, p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleSubmit} disabled={submitting || loadingServices}>
          Add Customer
        </Button>
      </Paper>

      <Dialog open={confirmNoIdOpen} onClose={() => setConfirmNoIdOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>No passport or Emirates ID</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {customer.name || 'This customer'} has no passport number or Emirates ID, so they won&apos;t be tracked as an
            employee and you won&apos;t get expiry reminders for their documents. Add the customer anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmNoIdOpen(false)}>Go Back</Button>
          <Button variant="contained" onClick={proceed}>Add Anyway</Button>
        </DialogActions>
      </Dialog>

      {/* Blocks the page for the whole chain so nothing is edited half-way. */}
      <Backdrop open={submitting} sx={{ zIndex: (t) => t.zIndex.modal + 1, color: '#fff', flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" />
        <Typography>{stage || 'Saving…'}</Typography>
      </Backdrop>
    </Box>
  );
};

export default AddWorkOrderCustomerPanel;
